<script module>
  export const drawer = new ModalState('drawer', { layer: 999 });
</script>

<script lang="ts">
  import Rotate from '~icons/mdi/cached';
  import Close from '~icons/mdi/close';
  import Drawer from 'svelte-drawer-component';
  import DailyIcon from '~/icons/DailyIcon.svelte';
  import ActionButton from '../components/ActionButton.svelte';
  import Home from '~icons/mdi/home';
  import ChartBar from '~icons/mdi/chart-bar';
  import ExternalLinkIcon from '~icons/feather/external-link';
  import Star from '~/icons/Star.svelte';

  import Feedback from '../modals/Feedback.svelte';
  import ChangelogButton from '../tutorial/ChangelogButton.svelte';
  import DiscordButton from './buttons/DiscordButton.svelte';
  import AuthButton from '../auth/AuthButton.svelte';
  import Archive from '~/calendar/Archive.svelte';
  import AudioControls from './AudioControls.svelte';
  import LeaderboardButton from '~/leaderboard/LeaderboardButton.svelte';
  import TutorialButton from '~/tutorial/TutorialButton.svelte';
  import { ModalState } from '~/lib/nav.svelte';
  import SplashLogo from './SplashLogo.svelte';
  import { navigating, page } from '$app/state';
  import MiniIcon from '~/icons/MiniIcon.svelte';
  import Settings from '~/modals/Settings.svelte';
  import ScrollShadow from './ScrollShadow.svelte';
  import { isPremium } from '~/lib/queries.svelte';
  import LinkButton from './LinkButton.svelte';
  import { dev } from '$app/env';

  const premium = isPremium();

  const handleClose = () => {
    drawer.pop();
  };

  const handleKeyDown = async (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };
</script>

<svelte:window on:keydown={handleKeyDown}></svelte:window>
<Drawer
  open={drawer.open && !navigating.from}
  size="320px"
  on:clickAway={handleClose}
>
  <div class="menu">
    <div class="title-container">
      <span style:width="24px"></span>
      <SplashLogo drawer />
      <button class="close" onclick={handleClose}>
        <Close size="1.5em" />
      </button>
    </div>
    <ScrollShadow>
      <div class="menu-items">
        <ActionButton href="/" outlink={false}>
          <Home />
          Home
        </ActionButton>
        <div
          class="action-group game-gradient {page.params.mode ??
            page.route.id?.slice(1)}"
        >
          {#if page.params.mode === 'mini'}
            <ActionButton buttonId="daily-mini">
              <MiniIcon selected />
              Mini
            </ActionButton>
            <TutorialButton />
            <Archive />
          {:else if page.params.mode === 'daily'}
            <ActionButton buttonId="daily">
              <DailyIcon selected />
              Daily
            </ActionButton>
            <TutorialButton />
            <Archive />
          {:else if page.route.id === '/classic'}
            <ActionButton buttonId="classic">
              <Star outlined drawer />
              Classic
            </ActionButton>
            <TutorialButton />
            <LeaderboardButton />
          {:else if page.route.id === '/twist'}
            <ActionButton buttonId="twist">
              <Rotate />
              Twist
            </ActionButton>
            <TutorialButton />
          {/if}
        </div>
        <div class="game-modes">
          <h1>Other Modes</h1>
          {#if page.params.mode !== 'mini'}
            <ActionButton buttonId="daily-mini" href="/mini" outlink={false}>
              <MiniIcon />
              Mini
            </ActionButton>
          {/if}
          {#if page.params.mode !== 'daily'}
            <ActionButton buttonId="daily" href="/daily" outlink={false}>
              <DailyIcon />
              Daily
            </ActionButton>
          {/if}
          {#if page.route.id !== '/classic'}
            <ActionButton buttonId="classic" href="/classic" outlink={false}>
              <Star outlined drawer />
              Classic
            </ActionButton>
          {/if}
          {#if page.route.id !== '/twist' && dev}
            <ActionButton
              buttonId="twist"
              href={premium.data ? '/twist' : '?premium#beta'}
              outlink={false}
            >
              <Rotate />
              Twist
            </ActionButton>
          {/if}
        </div>
        <div class="divider"></div>
        <h1>Game Options</h1>
        <Settings />
        <ChangelogButton />
        <AudioControls />
        <div class="divider"></div>
        <h1>Feedback & Forums</h1>
        <Feedback />
        <DiscordButton />
        {#if page.data.isAdmin}
          <div class="divider"></div>
          <h1>Admin</h1>
          <ActionButton href="/admin/analytics" outlink={false}>
            <ChartBar />
            Analytics
          </ActionButton>
        {/if}
        <!--
        <SupportButton />
        <TwitterButton />
        <RedditButton />
        -->
      </div>
    </ScrollShadow>
    <div class="footer">
      <div class="divider"></div>
      <AuthButton />
      <div class="conditions">
        <LinkButton href="/terms" outlink>
          <ExternalLinkIcon />Terms of Service</LinkButton
        >
        <LinkButton href="/privacy" outlink>
          <ExternalLinkIcon />Privacy Policy</LinkButton
        >
      </div>
    </div>
  </div>
</Drawer>

<style lang="scss">
  @use 'sass:color';
  @use '~/theme/include-media' as *;

  :global(.drawer.open) {
    z-index: 999 !important;
  }
  :global(html.dark-mode .drawer .overlay) {
    background: rgba(0, 0, 0, 0.5);
  }
  :global(html.dark-mode .drawer .panel) {
    background-color: #303237;
  }

  .close {
    color: currentColor;
    cursor: pointer;
  }

  .conditions {
    font-size: 0.8em;
    display: flex;
    justify-content: space-between;
  }

  .menu {
    display: grid;
    height: 100%;
    overflow: hidden;
    :global(.action) {
      border-radius: 1em;
      gap: 1em;
      padding: 0.75em 1em;
    }
  }

  .title-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5em;
    & button {
      margin: 0;
      padding: 0;
      background: none;
      border: none;
    }
  }

  .menu-items {
    height: 100%;
    overflow: auto;
    display: grid;
    text-align: center;
    gap: 0.5em;
    padding: 0.5em;
    overflow-x: hidden;
  }

  .game-modes {
    display: grid;
    gap: 0.5em;
  }

  h1 {
    margin: 0;
  }

  .action-group {
    border-radius: 1em;
    & :global(button) {
      background-color: transparent !important;
      width: 100%;
    }
    & :global(a) {
      background-color: transparent !important;
      width: 100%;
    }
    & :global(:not(:first-child):not(:last-child)) {
      border-top: none;
      border-radius: 0;
    }
    & :global(button:first-of-type) {
      text-decoration: none;
      border-radius: 1em 1em 0 0;
    }
    & :global(a:first-of-type) {
      text-decoration: none;
      border-radius: 1em 1em 0 0;
    }
    & :global(button:last-of-type) {
      border-top: none;
      border-radius: 0 0 1em 1em;
    }
    & :global(a:last-of-type) {
      border-top: none;
      border-radius: 0 0 1em 1em;
    }
  }

  .divider {
    border-top: 2px solid;
    margin: 0.5em -0.5em;
  }

  .footer {
    align-self: end;
    margin-top: -0.5em;
    padding: 0.5rem;
    padding-top: 0;
  }
</style>
